// Theme-world helpers for composed experiences.
//
// A composed experience defaults to an AI-picked *contrasting* theme world so
// it stands out from the surrounding course (Dylan's call, 2026-07-10): the
// composer emits `recommendedTheme` and this resolves it to the group's
// `themeOverride`. Mirrors the Library build path's `validStyle` gate
// (CreateRoom.runSingleBuild) so both surfaces share one allowlist.
//
// Note: composed experiences render at the standard themed tier — a theme world
// here changes colours/fonts, not the flagship per-block drama (that is the
// Polish track). The author can always re-theme or clear the override via the
// ScenarioGroupThemeChip in the builder.

import type { ThemeId } from '../../../components/course-builder/slides/slideTypes.js';

/** The six bespoke theme worlds — the only styles the AI may recommend, matching
 *  `propose_concepts` and the Library path's ALLOWED_STYLES. */
export const THEME_WORLD_IDS = [
  'neon-oracle',
  'obsidian',
  'aurora',
  'terra',
  'hyperpop',
  'risograph',
] as const;

export type ThemeWorldId = (typeof THEME_WORLD_IDS)[number];

const WORLD_SET: ReadonlySet<string> = new Set(THEME_WORLD_IDS);

/** Type guard: is this value one of the six theme worlds? */
export function isThemeWorld(value: unknown): value is ThemeWorldId {
  return typeof value === 'string' && WORLD_SET.has(value);
}

/**
 * Resolve a composed spec's AI-recommended theme to a group `themeOverride`.
 * Returns `undefined` (inherit the course theme) when the spec omits it or names
 * anything outside the six worlds — the same defensive gate the Library path
 * applies to `concept.recommendedStyle`.
 */
export function pickComposedThemeOverride(rawSpec: unknown): ThemeId | undefined {
  const rec = (rawSpec as { recommendedTheme?: unknown } | null)?.recommendedTheme;
  return isThemeWorld(rec) ? rec : undefined;
}
